/**
 * Portal V2 runtime feature flags.
 * - Journey draw tools: local (admin browser only)
 * - E-learning V2 images: global (API-backed, all V2 clients)
 */
import requestAxios from './requestAxios';

const JOURNEY_DRAW_KEY = 'dv2JourneyDev';
const IMAGES_CACHE_KEY = 'dv2ElearningImagesV2';

let imagesV2Memory = null;
let imagesV2Listeners = new Set();

function readConfigImagesDefault() {
    if (typeof window === 'undefined') return true;
    if (window.APP_CONFIG && typeof window.APP_CONFIG.useElearningImagesV2 === 'boolean') {
        return window.APP_CONFIG.useElearningImagesV2;
    }
    // Soft-launch default: V2 covers on when portal-v2 assets ship with the build
    return true;
}

function readCachedImagesFlag() {
    try {
        const raw = window.localStorage?.getItem(IMAGES_CACHE_KEY);
        if (raw === '1') return true;
        if (raw === '0') return false;
    } catch {
        /* ignore */
    }
    return null;
}

function writeCachedImagesFlag(enabled) {
    try {
        window.localStorage?.setItem(IMAGES_CACHE_KEY, enabled ? '1' : '0');
    } catch {
        /* ignore */
    }
}

function notifyImagesListeners() {
    imagesV2Listeners.forEach((fn) => {
        try {
            fn(areElearningImagesV2Enabled());
        } catch {
            /* ignore */
        }
    });
}

/** Subscribe to global images-flag changes (admin toggle / fetch). */
export function subscribeElearningImagesV2(listener) {
    imagesV2Listeners.add(listener);
    return () => imagesV2Listeners.delete(listener);
}

export function areElearningImagesV2Enabled() {
    if (typeof imagesV2Memory === 'boolean') return imagesV2Memory;
    const cached = readCachedImagesFlag();
    if (typeof cached === 'boolean') return cached;
    return readConfigImagesDefault();
}

export function setElearningImagesV2Local(enabled) {
    imagesV2Memory = Boolean(enabled);
    writeCachedImagesFlag(imagesV2Memory);
    notifyImagesListeners();
}

/**
 * Load global flag from API (all V2 clients). Falls back to cache / Config.
 */
export async function fetchElearningImagesV2Flag(token) {
    const { error, data } = await requestAxios({
        url: 'PortalV2Flags',
        method: 'get',
        token: token || '',
    });
    if (!error && data && typeof data.useElearningImagesV2 === 'boolean') {
        setElearningImagesV2Local(data.useElearningImagesV2);
        return data.useElearningImagesV2;
    }
    return areElearningImagesV2Enabled();
}

/**
 * Admin-only: persist global flag for every V2 client.
 */
export async function saveElearningImagesV2Flag(enabled, token) {
    const { error, data, status } = await requestAxios({
        url: 'PortalV2Flags',
        method: 'put',
        token,
        data: { useElearningImagesV2: Boolean(enabled) },
    });
    if (error) {
        return { ok: false, status, error };
    }
    const value =
        typeof data?.useElearningImagesV2 === 'boolean'
            ? data.useElearningImagesV2
            : Boolean(enabled);
    setElearningImagesV2Local(value);
    return { ok: true, value };
}

/** Local-only: journey path draw / edit tools */
export function readJourneyDrawEnabled() {
    try {
        if (typeof window === 'undefined') return false;
        if (window.__DV2_JOURNEY_DEV__ === true) return true;
        return window.localStorage?.getItem(JOURNEY_DRAW_KEY) === '1';
    } catch {
        return false;
    }
}

export function writeJourneyDrawEnabled(enabled) {
    try {
        if (enabled) {
            window.localStorage?.setItem(JOURNEY_DRAW_KEY, '1');
            window.__DV2_JOURNEY_DEV__ = true;
        } else {
            window.localStorage?.removeItem(JOURNEY_DRAW_KEY);
            window.__DV2_JOURNEY_DEV__ = false;
        }
    } catch {
        /* ignore */
    }
}
